import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useState,
} from "react";

import { useQueryClient } from "@tanstack/react-query";
import { updateUserAttribute, fetchAuthSession } from "aws-amplify/auth";
import dayjs from "dayjs";

import { getGetDailyTopPicksQueryKey } from "@api";

import i18n from "../../services/translation/i18n";

type TranslationContextValue = {
  locale: string;
  changeLocale: (locale: string) => void;
};

const TranslationContext = createContext<TranslationContextValue>({
  locale: "en",
  changeLocale: () => {},
});

export const TranslationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [currentLocale, setCurrentLocale] = useState<string>(i18n.language);
  const queryClient = useQueryClient();

  const handleLocaleChange = async (locale: string) => {
    await i18n.changeLanguage(locale);
    dayjs.locale(locale);
    setCurrentLocale(locale);
    const authSession = await fetchAuthSession();
    if (authSession.userSub) {
      try {
        await updateUserAttribute({
          userAttribute: {
            attributeKey: "custom:locale",
            value: locale,
          },
        });
        void queryClient.invalidateQueries({
          queryKey: getGetDailyTopPicksQueryKey(),
        });
      } catch (error) {
        console.log("Failed to update user locale", error);
      }
    }
  };

  return (
    <TranslationContext.Provider
      value={{ locale: currentLocale, changeLocale: handleLocaleChange }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslationContext = () => {
  const context = useContext(TranslationContext);

  if (!context) {
    throw new Error(
      "useTranslationContext must be used within a TranslationProvider",
    );
  }

  return context;
};

export const TranslationConsumer = TranslationContext.Consumer;
